import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { TextField, Dialog, DialogContent, DialogTitle, Button, DialogActions, Select, MenuItem, InputLabel, FormControl, Box } from '@mui/material';

import { createNewRequirement } from '../../../actions/requirementsActions';
import { buildRequirementPayload } from '../../../utils/buildRequirementPayload';
import MessageDialog from '../../../utils/MessageDialog';

// Dialog component for creating new requirements
const RequirementsCreateDialog = ({ open, onClose, companyRef }) => {
  // State for form data with initial empty values
  const [formData, setFormData] = useState({
    nombre: '',
    documento: '',
    celular: '',
    cargo: '',
    correo: '',
    ciudad: '',
    tipoPrueba: '',
    cliente: '',
    centro: '',
    tipoDocumento: ''
  });

  // State for message dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogMessage, setDialogMessage] = useState('');

  // Function to show message dialog with custom title and message
  const showDialog = (title, message) => {
    setDialogTitle(title);
    setDialogMessage(message);
    setDialogOpen(true);
  };

  const dispatch = useDispatch();

  // Handle dialog close event
  const handleClose = e => {
    e.preventDefault();
    onClose();
  };

  // Handle form input changes
  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Validate that all form fields are filled
  const validateForm = () => {
    const {
      nombre,
      documento,
      celular,
      cargo,
      correo,
      ciudad,
      tipoPrueba,
      cliente,
      centro,
      tipoDocumento
    } = formData;
    return (
      nombre &&
      documento &&
      celular &&
      cargo &&
      correo &&
      ciudad &&
      tipoPrueba &&
      cliente &&
      centro &&
      tipoDocumento
    );
  };

  // Handle form submission
  const handleSubmit = e => {
    e.preventDefault();
    if (validateForm()) {
      // Build payload and dispatch action to create new requirement
      const payload = buildRequirementPayload(formData, companyRef);
      dispatch(createNewRequirement({ requirement: payload }))
        .then(() => {
          showDialog('Éxito', '✅ Requerimiento creado exitosamente');
          onClose();
          // Reset form fields after successful submission
          setFormData({
            nombre: '',
            documento: '',
            celular: '',
            cargo: '',
            correo: '',
            ciudad: '',
            tipoPrueba: '',
            cliente: '',
            centro: '',
            tipoDocumento: ''
          });
        })
        .catch(() => {
          showDialog('Error', '❌ Error al crear el requerimiento. Por favor, intente de nuevo.');
        });
    } else {
      showDialog('Error', '❌ Por favor, complete todos los campos del formulario.');
    }
  };

  return (
    <>
      {/* Main dialog for creating new requirements */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Nuevo Requerimiento</DialogTitle>

        <DialogContent>
          {/* Client field */}
          <TextField
            label="Cliente"
            name="cliente"
            value={formData.cliente}
            onChange={handleChange}
            margin="dense"
            required
          />
          
          {/* Name field */}
          <TextField
            label="Nombre"
            name="nombre"
            value={formData.nombre}
            onChange={handleChange}
            fullWidth
            margin="dense"
            required
          />
          
          {/* Document type and number fields */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl fullWidth margin="dense">
              <InputLabel>Tipo de documento</InputLabel>
              <Select
                name="tipoDocumento"
                value={formData.tipoDocumento}
                onChange={handleChange}
                required
              >
                <MenuItem value="CC">Cédula de Ciudadanía</MenuItem>
                <MenuItem value="TI">Tarjeta de Identidad</MenuItem>
                <MenuItem value="RC">Registro Civil</MenuItem>
                <MenuItem value="CE">Cédula de Extranjería</MenuItem>
                <MenuItem value="PP">Pasaporte</MenuItem>
                <MenuItem value="NT">NIT</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Documento de identidad"
              name="documento"
              value={formData.documento}
              onChange={handleChange}
              margin="dense"
              required
            />
          </Box>

          {/* Phone and email fields */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Celular"
              name="celular"
              value={formData.celular}
              onChange={handleChange}
              type="numeric"
              fullWidth
              margin="dense"
              required
            />
            <TextField
              label="Correo notificación"
              name="correo"
              value={formData.correo}
              onChange={handleChange}
              type="email-address"
              fullWidth
              margin="dense"
              required
            />
          </Box>

          {/* City and test type fields */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Ciudad"
              name="ciudad"
              value={formData.ciudad}
              onChange={handleChange}
              fullWidth
              margin="dense"
              required
            />
            <FormControl fullWidth margin="dense">
              <InputLabel>Tipo prueba</InputLabel>
              <Select
                name="tipoPrueba"
                value={formData.tipoPrueba}
                onChange={handleChange}
                required
              >
                <MenuItem value="Rutina">Rutina</MenuItem>
                <MenuItem value="Pre-empleo">Pre-empleo</MenuItem>
                <MenuItem value="Específica">Específica</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Position and cost center fields */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Cargo"
              name="cargo"
              value={formData.cargo}
              onChange={handleChange}
              fullWidth
              margin="dense"
              required
            />
            <TextField
              label="Centro de costos"
              name="centro"
              value={formData.centro}
              onChange={handleChange}
              margin="dense"
              required
            />
          </Box>
        </DialogContent>

        {/* Dialog action buttons */}
        <DialogActions>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
            <Button onClick={handleClose} color="primary" style={{ marginRight: '10px' }}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} type="submit" color="primary">
              Solicitar
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Message dialog for showing success/error messages */}
      <MessageDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={dialogTitle}
        message={dialogMessage}
      />
    </>
  );
};

export default RequirementsCreateDialog;